import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import type { ViewingAs } from "../types";
import { useRunStore } from "../state/runStore";
import { useTeamStore } from "../state/teamStore";
import { slugifySeriesName } from "../utils/slug";
import NotificationBell from "./NotificationBell";
import ErrorBoundary from "./ErrorBoundary";

interface Crumb {
  label: string;
  path: string;
}

export default function AppShell() {
  const { user, logout } = useAuthStore();
  const { teams } = useTeamStore();
  const { trackWeeks } = useRunStore();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) {
        setMenuOpen(false);
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const crumbs = useMemo(
    () => buildBreadcrumbs(location.pathname, params, teams, trackWeeks),
    [location.pathname, params, teams, trackWeeks]
  );

  const displayName = user?.displayName || user?.email || user?.iracingCustId || "Team Member";
  const userInitial = displayName.trim().charAt(0).toUpperCase() || "X";

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  return (
    <div style={styles.shell}>
      {user?.viewingAs && <SupportViewBanner viewingAs={user.viewingAs} />}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <Link to="/teams" style={styles.brand}>
            <div style={styles.brandMark}>
              <span style={styles.brandMarkCore}>X</span>
            </div>
            <div style={styles.brandCopy}>
              <span style={styles.brandEyebrow}>Project</span>
              <span style={styles.brandName}>StintSense</span>
            </div>
          </Link>

          <div style={styles.headerSpacer} />

          <div style={styles.desktopUser} className="appshell-user-area">
            {user?.isPlatformAdmin && (
              <Link
                to="/admin"
                style={{
                  alignSelf: "center",
                  padding: "0 0.7rem",
                  height: "2.7rem",
                  display: "inline-flex",
                  alignItems: "center",
                  borderRadius: "var(--r-md)",
                  border: "1px solid var(--rule-strong)",
                  color: "var(--text-default)",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                }}
              >
                Admin
              </Link>
            )}
            <NotificationBell />
            <Link to="/settings" style={styles.userChip}>
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" style={styles.avatarImage} />
              ) : (
                <span style={styles.avatarFallback}>{userInitial}</span>
              )}
              <span style={styles.userText}>
                <span style={styles.userLabel}>Account</span>
                <span style={styles.userName}>{displayName}</span>
              </span>
            </Link>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSignOut}
              style={{ height: "2.7rem", minHeight: 0 }}
            >
              Sign out
            </button>
          </div>

          <button
            type="button"
            className="appshell-hamburger"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            style={styles.hamburger}
          >
            <span style={{ ...styles.hamburgerLine, ...(menuOpen ? styles.hamburgerLineTopOpen : {}) }} />
            <span style={{ ...styles.hamburgerLine, ...(menuOpen ? styles.hamburgerLineMiddleOpen : {}) }} />
            <span style={{ ...styles.hamburgerLine, ...(menuOpen ? styles.hamburgerLineBottomOpen : {}) }} />
          </button>
        </div>
      </header>

      {menuOpen && (
        <>
          <div style={styles.mobilePanel}>
            <Link to="/settings" style={styles.mobileAccount} onClick={() => setMenuOpen(false)}>
              <span style={styles.avatarFallback}>{userInitial}</span>
              <span style={styles.userText}>
                <span style={styles.userLabel}>Signed in as</span>
                <span style={styles.userName}>{displayName}</span>
              </span>
            </Link>
            {/* The Admin link lives only in the desktop user area (hidden ≤768px) —
                surface it here so the console is reachable on mobile. (The notification
                bell needs a mobile-positioned dropdown variant — tracked follow-up.) */}
            {user?.isPlatformAdmin && (
              <Link
                to="/admin"
                className="btn btn-secondary"
                style={{ width: "100%" }}
                onClick={() => setMenuOpen(false)}
              >
                Admin
              </Link>
            )}
            <button type="button" className="btn btn-secondary" onClick={handleSignOut} style={{ width: "100%" }}>
              Sign out
            </button>
          </div>
          <button type="button" aria-label="Close menu" style={styles.mobileBackdrop} onClick={() => setMenuOpen(false)} />
        </>
      )}

      {crumbs.length > 0 && (
        <div style={styles.breadcrumbWrap}>
          <div style={styles.breadcrumbInner}>
            {crumbs.map((crumb, index) => (
              <span key={crumb.path} style={styles.crumbSegment}>
                {index > 0 && <span style={styles.crumbDivider}>/</span>}
                {index < crumbs.length - 1 ? (
                  <Link to={crumb.path} style={styles.crumbLink}>
                    {crumb.label}
                  </Link>
                ) : (
                  <span style={styles.crumbCurrent}>{crumb.label}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <main style={styles.main}>
        {/* Keyed by path so a crashed page resets when you navigate away, and a
            render error shows a message instead of blanking the whole app. */}
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}

/**
 * Admin "view as team" hazard strip. DELIBERATELY off-brand (safety amber/black —
 * decision 0005 reserves the race-flag colors, and product chrome never looks like
 * this) so a support session can't be mistaken for normal use. Server-driven via
 * /me `viewingAs`; pinned to the viewport top, no dismiss.
 */
function SupportViewBanner({ viewingAs }: { viewingAs: ViewingAs }) {
  const { exitImpersonation } = useAuthStore();
  const [now, setNow] = useState(() => Date.now());
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const expiresMs = Date.parse(viewingAs.expiresAt);
  const remaining = Number.isFinite(expiresMs) ? Math.max(0, Math.floor((expiresMs - now) / 1000)) : 0;
  const expired = remaining <= 0;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  const handleExit = async () => {
    setExiting(true);
    await exitImpersonation();
    window.location.assign("/admin");
  };

  return (
    <div style={bannerStyles.hazard}>
      <div style={bannerStyles.bar} role={expired ? "status" : undefined}>
        {expired ? (
          <>
            <span style={bannerStyles.callsign}>Support view ended</span>
            <button
              type="button"
              style={bannerStyles.exitBtn}
              onClick={() => window.location.assign("/admin")}
            >
              Return to admin
            </button>
          </>
        ) : (
          <>
            <span style={bannerStyles.text}>
              <span style={bannerStyles.callsign}>SUPPORT VIEW</span>
              {" · "}{viewingAs.teamName || "Team"}{" · read-only · all access recorded · expires "}
              <span style={bannerStyles.clock}>{mm}:{ss}</span>
            </span>
            <button type="button" style={bannerStyles.exitBtn} onClick={() => void handleExit()} disabled={exiting}>
              {exiting ? "Exiting…" : "Exit support view"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Off-brand on purpose: raw hex, no Pitwall tokens. Amber #ffb200 / near-black.
const bannerStyles: Record<string, React.CSSProperties> = {
  hazard: {
    position: "sticky",
    top: 0,
    zIndex: 200,
    background: "repeating-linear-gradient(-45deg, #141414 0 12px, #ffb200 12px 24px)",
    padding: "5px 0",
  },
  bar: {
    background: "#ffb200",
    color: "#141414",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "0.35rem 1rem",
    padding: "0.45rem 1rem",
    textAlign: "center",
  },
  text: {
    fontFamily: "var(--font-body)",
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  callsign: {
    fontFamily: "var(--font-body)",
    fontSize: "0.875rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
  },
  clock: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 800,
  },
  exitBtn: {
    background: "#141414",
    color: "#ffb200",
    border: "none",
    borderRadius: "4px",
    padding: "0.3rem 0.8rem",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};

function buildBreadcrumbs(
  pathname: string,
  params: Record<string, string | undefined>,
  teams: { id: string; name: string }[],
  trackWeeks: {
    id: string;
    teamId: string;
    trackName: string;
    seriesName: string | null;
    weekNumber: number | null;
    displayWeekNumber?: number | null;
  }[]
): Crumb[] {
  if (pathname === "/" || pathname === "/teams") {
    return [];
  }

  const crumbs: Crumb[] = [{ label: "Teams", path: "/teams" }];
  const teamId = params.teamId || extractParam(pathname, "teams");
  if (!teamId) return crumbs;

  const team = teams.find((entry) => entry.id === teamId);
  crumbs.push({ label: team?.name || "Team", path: `/teams/${teamId}` });

  const seriesSlug = params.seriesSlug || extractParam(pathname, "series");
  if (!seriesSlug) return crumbs;

  const seriesWeek =
    trackWeeks.find((week) => week.teamId === teamId && slugifySeriesName(week.seriesName) === seriesSlug) ||
    trackWeeks.find((week) => slugifySeriesName(week.seriesName) === seriesSlug);

  crumbs.push({
    label: seriesWeek?.seriesName || unslugify(seriesSlug),
    path: `/teams/${teamId}/series/${seriesSlug}`,
  });

  const trackWeekId = params.trackWeekId || extractParam(pathname, "weeks");
  if (!trackWeekId) return crumbs;

  const currentWeek = trackWeeks.find((week) => week.id === trackWeekId);
  const weekLabel = currentWeek
    ? `${currentWeek.trackName}${(currentWeek.displayWeekNumber ?? currentWeek.weekNumber) != null ? ` (Wk ${currentWeek.displayWeekNumber ?? currentWeek.weekNumber})` : ""}`
    : "Week";
  const weekBasePath = `/teams/${teamId}/series/${seriesSlug}/weeks/${trackWeekId}`;
  crumbs.push({ label: weekLabel, path: `${weekBasePath}/overview` });

  const toolMatch = pathname.match(/\/weeks\/[^/]+\/([^/]+)/);
  const tool = toolMatch?.[1];
  if (!tool || tool === "overview") return crumbs;

  const toolLabels: Record<string, string> = {
    drivers: "Drivers",
    master: "Master",
    "head-to-head": "Head-to-Head",
    fuel: "Fuel",
  };

  crumbs.push({
    label: toolLabels[tool] || unslugify(tool),
    path: `${weekBasePath}/${tool}`,
  });

  return crumbs;
}

function extractParam(pathname: string, segment: string): string | undefined {
  const parts = pathname.split("/");
  const index = parts.indexOf(segment);
  if (index >= 0 && index + 1 < parts.length) {
    return parts[index + 1];
  }
  return undefined;
}

function unslugify(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: "100vh",
    background: "transparent",
  },
  header: {
    padding: "0.9rem 1rem",
    background: "var(--ink-1)",
    borderBottom: "1px solid var(--rule)",
  },
  headerInner: {
    width: "min(var(--container-wide-width), calc(100vw - 2rem))",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  brand: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.9rem",
    textDecoration: "none",
  },
  brandMark: {
    width: "2.6rem",
    height: "2.6rem",
    borderRadius: "10px",
    background: "var(--ink-2)",
    border: "1px solid var(--rule-strong)",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brandMarkCore: {
    fontFamily: "var(--font-display)",
    fontWeight: 800,
    fontSize: "1.2rem",
    color: "var(--text-strong)",
    letterSpacing: "-0.08em",
  },
  brandCopy: {
    display: "flex",
    flexDirection: "column",
    gap: "0.1rem",
  },
  brandEyebrow: {
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    letterSpacing: 0,
    textTransform: "none",
    color: "var(--text-muted)",
  },
  brandName: {
    fontFamily: "var(--font-display)",
    fontSize: "1.35rem",
    fontWeight: 700,
    letterSpacing: "-0.06em",
    color: "var(--text-strong)",
  },
  headerSpacer: {
    flex: 1,
  },
  desktopUser: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  userChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.6rem",
    height: "2.7rem",
    padding: "0 0.7rem 0 0.45rem",
    borderRadius: "var(--r-md)",
    textDecoration: "none",
    background: "var(--ink-2)",
    border: "1px solid var(--rule)",
  },
  avatarImage: {
    width: "2rem",
    height: "2rem",
    borderRadius: "999px",
    objectFit: "cover",
  },
  avatarFallback: {
    width: "2rem",
    height: "2rem",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--live-soft)",
    border: "1px solid var(--live-edge)",
    fontFamily: "var(--font-display)",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "var(--live-strong)",
    flexShrink: 0,
  },
  userText: {
    display: "flex",
    flexDirection: "column",
    gap: "0.1rem",
    minWidth: 0,
  },
  userLabel: {
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    letterSpacing: 0,
    textTransform: "none",
    color: "var(--text-muted)",
  },
  userName: {
    fontFamily: "var(--font-body)",
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "var(--text-default)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "18rem",
  },
  hamburger: {
    width: "2.9rem",
    height: "2.9rem",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--rule-strong)",
    background: "var(--ink-2)",
    padding: 0,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "0.25rem",
  },
  hamburgerLine: {
    width: "1rem",
    height: "2px",
    borderRadius: "999px",
    background: "var(--text-default)",
    transition: "transform 160ms ease, opacity 160ms ease",
  },
  hamburgerLineTopOpen: {
    transform: "translateY(6px) rotate(45deg)",
  },
  hamburgerLineMiddleOpen: {
    opacity: 0,
  },
  hamburgerLineBottomOpen: {
    transform: "translateY(-6px) rotate(-45deg)",
  },
  mobilePanel: {
    position: "fixed",
    top: "5.15rem",
    left: "1rem",
    right: "1rem",
    zIndex: 90,
    padding: "1rem",
    borderRadius: "var(--r-lg)",
    background: "var(--ink-1)",
    border: "1px solid var(--rule-strong)",
    boxShadow: "var(--shadow-lg)",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  mobileAccount: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    textDecoration: "none",
  },
  mobileBackdrop: {
    position: "fixed",
    inset: 0,
    top: "4.8rem",
    background: "rgba(10, 12, 16, 0.6)",
    border: "none",
    padding: 0,
    margin: 0,
    zIndex: 85,
  },
  breadcrumbWrap: {
    borderBottom: "1px solid var(--rule)",
    background: "var(--ink-0)",
  },
  breadcrumbInner: {
    width: "min(var(--container-wide-width), calc(100vw - 2rem))",
    margin: "0 auto",
    minHeight: "2.25rem",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.2rem",
    padding: "0.15rem 0",
  },
  crumbSegment: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.2rem",
    minWidth: 0,
  },
  crumbDivider: {
    margin: "0 0.5rem",
    color: "var(--text-muted)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.8125rem",
  },
  crumbLink: {
    color: "var(--text-secondary)",
    textDecoration: "none",
    fontFamily: "var(--font-body)",
    fontSize: "0.9rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "16rem",
  },
  crumbCurrent: {
    color: "var(--text-strong)",
    fontFamily: "var(--font-body)",
    fontSize: "0.9rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "28rem",
  },
  main: {
    minHeight: "calc(100vh - 4.8rem)",
  },
};
